import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Lock } from 'lucide-react';
import { Logo } from '../Logo';
import { useSiteSettings } from '../../hooks/useSiteSettings';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { settings } = useSiteSettings();

  return (
    <footer data-testid="footer" className="bg-primary-dark text-white">
      <div className="container-custom section-spacing">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            {/* Logo in footer */}
            <div className="mb-6">
              {settings.logo_url ? (
                <img 
                  src={settings.logo_url}
                  alt="Azienda Agricola Le Bontà del Vesuvio"
                  className="h-20 w-auto object-contain rounded-lg bg-white p-2"
                />
              ) : (
                <Logo isLight />
              )}
            </div>
            <p className="font-body text-white/70 leading-relaxed mb-6">
              <strong className="text-white/90">Azienda Agricola Le Bontà del Vesuvio</strong> —
              pomodorini napoletani, conserve artigianali e specialità tipiche campane
              dalla terra vulcanica del Vesuvio, Ercolano (NA).
            </p>
            <a
              href="https://www.facebook.com/profile.php?id=100091821963346"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="facebook-link"
              className="inline-flex items-center space-x-2 text-white/70 hover:text-accent transition-colors"
            >
              <Facebook className="w-5 h-5" />
              <span className="font-body text-sm">Seguici su Facebook</span>
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-white mb-6">
              Link Rapidi
            </h4>
            <ul className="space-y-3">
              {[
                { name: 'Chi Siamo', path: '/chi-siamo' },
                { name: 'Negozio', path: '/negozio' },
                { name: 'Galleria', path: '/galleria' },
                { name: 'News', path: '/news' },
                { name: 'Contatti', path: '/contatti' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="font-body text-white/70 hover:text-accent transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-white mb-6">
              Prodotti del Vesuvio
            </h4>
            <ul className="space-y-3">
              {[
                { name: 'Pomodorino Napoletano', cat: 'freschi' },
                { name: 'Specialità Campane', cat: 'specialita' },
                { name: 'Conserve Artigianali Vesuviane', cat: 'conserve' },
              ].map(({ name, cat }) => (
                <li key={cat}>
                  <Link
                    to={`/prodotti?cat=${cat}`}
                    className="font-body text-white/70 hover:text-accent transition-colors"
                  >
                    {name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-white mb-6">
              Contatti
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <span className="font-body text-white/70">
                  Via ex Cook 42, Ercolano (NA), 80056
                </span>
              </li>
              <li>
                <a
                  href="tel:+393382523925"
                  className="flex items-center space-x-3 text-white/70 hover:text-accent transition-colors"
                >
                  <Phone className="w-5 h-5 text-accent" />
                  <span className="font-body">+39 338 252 3925</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:lebontadelvesuvio1@gmail.com"
                  className="flex items-center space-x-3 text-white/70 hover:text-accent transition-colors"
                >
                  <Mail className="w-5 h-5 text-accent" />
                  <span className="font-body text-sm">lebontadelvesuvio1@gmail.com</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="font-body text-sm text-white/50">
              © {currentYear} Azienda Agricola Le Bontà del Vesuvio, Ercolano (NA) — Pomodorino Napoletano, Conserve Artigianali Campane, Specialità del Vesuvio. Tutti i diritti riservati.
            </p>
            <div className="flex items-center gap-4">
              {settings.orari && (
                <p className="font-body text-sm text-white/50">
                  {settings.orari}
                </p>
              )}
              <Link
                to="/admin"
                title="Area amministrativa"
                className="text-white/20 hover:text-white/50 transition-colors"
              >
                <Lock className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
